import type { FastifyReply, FastifyRequest } from 'fastify'
import type { ProfileService } from './profile.service.js'
import { ApiResponse } from '../../utils/response'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('ProfileController')

export class ProfileController {
  constructor(private readonly profileService: ProfileService) {}

  async getProfile(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Unauthorized profile request')
        return ApiResponse.unauthorized(reply)
      }

      logger.debug('Getting user profile', { userId })
      const profile = await this.profileService.getProfile(userId, {
        userAgent: request.headers['user-agent'],
        ip: request.ip
      })
      logger.info('User profile retrieved', { userId, displayName: profile.displayName })
      return reply.send(profile)
    } catch (error) {
      logger.error('Failed to get profile', { error: (error as Error).message })
      request.log.error({ err: error }, 'profile_get_failed')
      return ApiResponse.internalError(reply, 'Failed to load profile')
    }
  }

  /**
   * Lightweight profile endpoint for dashboard
   * Returns only essential fields: id, email, displayName, photo, plan, quota
   * Excludes: metrics, behavior analysis, sessions, login history
   */
  async getProfileLight(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Unauthorized light profile request')
        return ApiResponse.unauthorized(reply)
      }

      const profile = await this.profileService.getProfile(userId, {
        userAgent: request.headers['user-agent'],
        ip: request.ip
      })

      // Return only essential fields (60% smaller payload)
      const lightProfile = {
        id: profile.id,
        email: profile.email,
        displayName: profile.displayName,
        photoURL: profile.photoURL,
        avatarUrl: profile.avatarUrl,
        account: {
          status: profile.account?.status,
          roles: profile.account?.roles,
          plan: profile.account?.plan,
          quota: profile.account?.quota,
          subscriptionStatus: profile.account?.subscriptionStatus
        }
      }

      return reply.send(lightProfile)
    } catch (error) {
      logger.error('Failed to get light profile', { error: (error as Error).message })
      request.log.error({ err: error }, 'profile_get_light_failed')
      return ApiResponse.internalError(reply, 'Failed to load profile')
    }
  }

  async updateProfile(
    request: FastifyRequest<{ Body: Record<string, unknown> }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Unauthorized profile update request')
        return ApiResponse.unauthorized(reply)
      }

      logger.info('Updating user profile', { userId })
      await this.profileService.updateProfile(userId, request.body ?? {})
      logger.info('User profile updated', { userId })
      return reply.send({ success: true })
    } catch (error) {
      logger.error('Failed to update profile', { error: (error as Error).message })
      request.log.error({ err: error }, 'profile_update_failed')
      return ApiResponse.badRequest(reply, 'Failed to update profile')
    }
  }

  async updateProfileIcon(
    request: FastifyRequest<{ Body: { iconId?: string; color?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Unauthorized icon update request')
        return ApiResponse.unauthorized(reply)
      }

      const iconId = request.body?.iconId
      if (!iconId) {
        logger.warn('Missing iconId', { userId })
        return ApiResponse.badRequest(reply, 'iconId is required')
      }

      const color = request.body?.color

      logger.info('Updating profile icon', { userId, iconId, color })
      const user = await this.profileService.updateProfileIcon(userId, iconId, color)
      logger.info('Profile icon updated', { userId })
      return reply.send(user)
    } catch (error) {
      logger.error('Failed to update profile icon', { error: (error as Error).message })
      request.log.error({ err: error }, 'profile_icon_update_failed')
      return ApiResponse.badRequest(reply, 'Failed to update profile icon')
    }
  }

  async updatePreferences(
    request: FastifyRequest<{ Body: Record<string, unknown> }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        logger.warn('Unauthorized preferences update request')
        return ApiResponse.unauthorized(reply)
      }

      logger.info('Updating user preferences', { userId })
      const preferences = await this.profileService.updatePreferences(userId, request.body ?? {})
      logger.info('User preferences updated', { userId })
      return reply.send({ success: true, preferences })
    } catch (error) {
      logger.error('Failed to update preferences', { error: (error as Error).message })
      return ApiResponse.badRequest(reply, 'Failed to update preferences')
    }
  }

  async recordInteraction(
    request: FastifyRequest<{ Body: Record<string, unknown> }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      await this.profileService.recordInteraction(userId, request.body ?? {})
      return reply.code(201).send({ success: true })
    } catch (error) {
      request.log.error({ err: error }, 'preferences_interaction_failed')
      return ApiResponse.badRequest(reply, 'Failed to record interaction')
    }
  }

  async getRecommendations(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const recommendations = await this.profileService.getCategoryRecommendations(userId)
      return reply.send({ recommendations })
    } catch (error) {
      request.log.error({ err: error }, 'preferences_recommendations_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch recommendations')
    }
  }

  async revokeSession(
    request: FastifyRequest<{ Body: { sessionId?: string } }>,
    reply: FastifyReply
  ) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const sessionId = request.body?.sessionId
      if (!sessionId) {
        return ApiResponse.badRequest(reply, 'sessionId is required')
      }
      await this.profileService.revokeSession(userId, sessionId)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error({ err: error }, 'session_revoke_failed')
      return ApiResponse.internalError(reply, 'Failed to revoke session')
    }
  }

  async requestDataExport(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      await this.profileService.requestDataExport(userId)
      return reply.code(202).send({ success: true })
    } catch (error) {
      request.log.error({ err: error }, 'data_export_failed')
      return ApiResponse.internalError(reply, 'Failed to request data export')
    }
  }
}
